import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  QrCode,
  Download,
  Printer,
  Copy,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  Filter,
  Grid3X3,
  List,
  Loader2,
  Coffee,
  Umbrella,
  Link as LinkIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QRManager() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const printRef = useRef(null);
  
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState('all');
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState({});
  const [loadingQrs, setLoadingQrs] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState([]);
  const [copiedUrl, setCopiedUrl] = useState(null);

  useEffect(() => {
    fetchSpaces();
  }, []);

  const fetchSpaces = async () => {
    try {
      const spacesResponse = await axios.get(`${API}/spaces`, { withCredentials: true });
      const spacesData = spacesResponse.data;
      setSpaces(spacesData);
      
      // Fetch units for each space
      const allUnits = [];
      for (const space of spacesData) {
        try {
          const unitsResponse = await axios.get(`${API}/spaces/${space.space_id}/units`, { withCredentials: true });
          const spaceUnits = (unitsResponse.data || [])
            .filter(u => !['door', 'bathroom', 'divider', 'bar_counter'].includes(u.type))
            .map(u => ({
              ...u,
              space_name: space.name,
              space_id: space.space_id
            }));
          allUnits.push(...spaceUnits);
        } catch (err) {
          console.error(`Error fetching units for space ${space.space_id}:`, err);
        }
      }
      
      setUnits(allUnits);
    } catch (error) {
      console.error('Error fetching spaces:', error);
      toast.error('Error al cargar espacios');
    } finally {
      setLoading(false);
    }
  };

  const loadQrData = async (unitId) => {
    if (qrData[unitId]) return; // Already loaded
    
    setLoadingQrs(prev => ({ ...prev, [unitId]: true }));
    try {
      const response = await axios.get(`${API}/units/${unitId}/qr-data`, { withCredentials: true });
      setQrData(prev => ({ ...prev, [unitId]: response.data }));
    } catch (error) {
      console.error('Error loading QR:', error);
      toast.error('Error al cargar QR');
    } finally {
      setLoadingQrs(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const copyToClipboard = async (url, unitId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(unitId);
      toast.success('URL copiada');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const downloadQr = async (unitId, unitName) => {
    if (!qrData[unitId]) {
      await loadQrData(unitId);
    }
    
    const data = qrData[unitId];
    if (!data?.qr_image) return;
    
    const link = document.createElement('a');
    link.href = data.qr_image;
    link.download = `QR_${unitName.replace(/\s+/g, '_')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR descargado');
  };

  const openQrUrl = (url) => {
    window.open(url, '_blank');
  };

  const togglePrintSelection = (unitId) => {
    setSelectedForPrint(prev => 
      prev.includes(unitId) 
        ? prev.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const selectAllForPrint = () => {
    const filtered = filteredUnits.map(u => u.unit_id);
    setSelectedForPrint(filtered);
  };

  const handlePrintSelected = async () => {
    // Load all QR data for selected units
    setShowPrintDialog(true);
    
    for (const unitId of selectedForPrint) {
      if (!qrData[unitId]) {
        await loadQrData(unitId);
      }
    }
  };

  const printQrs = () => {
    const printContent = selectedForPrint.map(unitId => {
      const unit = units.find(u => u.unit_id === unitId);
      const data = qrData[unitId];
      if (!unit || !data) return '';
      
      return `
        <div class="qr-item">
          <img src="${data.qr_image}" alt="QR ${unit.name}" />
          <p class="unit-name">${unit.name}</p>
          <p class="space-name">${unit.space_name}</p>
        </div>
      `;
    }).join('');
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Códigos QR</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; }
          .print-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
          }
          .qr-item {
            text-align: center;
            padding: 15px;
            border: 1px dashed #ccc;
            border-radius: 8px;
            page-break-inside: avoid;
          }
          .qr-item img {
            width: 150px;
            height: 150px;
          }
          .unit-name {
            font-weight: bold;
            font-size: 14px;
            margin-top: 8px;
          }
          .space-name {
            font-size: 12px;
            color: #666;
          }
          @media print {
            .print-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
        </style>
      </head>
      <body>
        <div class="print-grid">
          ${printContent}
        </div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setShowPrintDialog(false);
    setSelectedForPrint([]);
  };

  const filteredUnits = selectedSpace === 'all' 
    ? units 
    : units.filter(u => u.space_id === selectedSpace);

  const getUnitIcon = (type) => {
    switch (type) {
      case 'umbrella': return Umbrella;
      case 'table':
      default: return Coffee;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando QRs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              Gestión de QRs
            </h1>
            <p className="text-sm text-muted-foreground">
              Genera, descarga e imprime códigos QR para tus mesas
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchSpaces}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          {selectedForPrint.length > 0 && (
            <Button 
              onClick={handlePrintSelected}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir ({selectedForPrint.length})
            </Button>
          )}
        </div>
      </header>

      {/* Toolbar */}
      <div className="border-b border-border bg-card/50 px-4 md:px-6 py-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Select value={selectedSpace} onValueChange={setSelectedSpace}>
              <SelectTrigger className="w-56">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por espacio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  Todos los espacios ({units.length} unidades)
                </SelectItem>
                {spaces.map((space) => (
                  <SelectItem key={space.space_id} value={space.space_id}>
                    {space.name} ({space.units?.filter(u => !['door', 'bathroom', 'divider', 'bar_counter'].includes(u.type)).length || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={selectAllForPrint}>
              Seleccionar todos
            </Button>
            {selectedForPrint.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedForPrint([])}>
                Deseleccionar ({selectedForPrint.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="p-4 md:p-6">
        {filteredUnits.length === 0 ? (
          <div className="text-center py-12">
            <QrCode className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No hay unidades en este espacio</p>
            <p className="text-sm text-muted-foreground mt-1">
              Crea mesas o sombrillas en el Editor de Espacios
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredUnits.map((unit) => {
              const Icon = getUnitIcon(unit.type);
              const isSelected = selectedForPrint.includes(unit.unit_id);
              const isLoading = loadingQrs[unit.unit_id];
              const data = qrData[unit.unit_id];
              
              return (
                <Card 
                  key={unit.unit_id}
                  className={`relative overflow-hidden transition-all cursor-pointer hover:shadow-lg ${
                    isSelected ? 'ring-2 ring-primary border-primary' : ''
                  }`}
                  onClick={() => togglePrintSelection(unit.unit_id)}
                  onMouseEnter={() => loadQrData(unit.unit_id)}
                  data-testid={`qr-card-${unit.unit_id}`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 z-10">
                      <CheckCircle className="w-5 h-5 text-primary fill-primary/20" />
                    </div>
                  )}
                  
                  <CardContent className="p-4">
                    <div className="text-center">
                      {/* QR Preview */}
                      <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                        {isLoading ? (
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        ) : data?.qr_image ? (
                          <img 
                            src={data.qr_image} 
                            alt={`QR ${unit.name}`}
                            className="w-full h-full object-contain p-2"
                          />
                        ) : (
                          <QrCode className="w-12 h-12 text-muted-foreground" />
                        )}
                      </div>

                      {/* Unit Info */}
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-semibold">{unit.name}</h3>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {unit.space_name}
                      </Badge>

                      {/* Actions */}
                      <div className="flex justify-center gap-1 mt-3">
                        <Button
                          size="icon"
                          variant="outline"
                          className="w-8 h-8"
                          onClick={(e) => { e.stopPropagation(); downloadQr(unit.unit_id, unit.name); }}
                          title="Descargar QR"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="w-8 h-8"
                          onClick={(e) => { e.stopPropagation(); data?.url && copyToClipboard(data.url, unit.unit_id); }}
                          title="Copiar URL"
                        >
                          {copiedUrl === unit.unit_id ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="w-8 h-8"
                          onClick={(e) => { e.stopPropagation(); data?.url && openQrUrl(data.url); }}
                          title="Abrir menú"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {filteredUnits.map((unit) => {
                  const Icon = getUnitIcon(unit.type);
                  const isSelected = selectedForPrint.includes(unit.unit_id);
                  const isLoading = loadingQrs[unit.unit_id];
                  const data = qrData[unit.unit_id];
                  
                  return (
                    <div 
                      key={unit.unit_id}
                      className={`flex items-center gap-4 p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => togglePrintSelection(unit.unit_id)}
                      onMouseEnter={() => loadQrData(unit.unit_id)}
                    >
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      }`}>
                        {isSelected && <CheckCircle className="w-3 h-3 text-primary-foreground" />}
                      </div>

                      {/* QR Thumbnail */}
                      <div className="w-16 h-16 bg-muted rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                        {isLoading ? (
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        ) : data?.qr_image ? (
                          <img 
                            src={data.qr_image} 
                            alt={`QR ${unit.name}`}
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <QrCode className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <h3 className="font-semibold">{unit.name}</h3>
                          <Badge variant="secondary" className="text-xs">
                            {unit.space_name}
                          </Badge>
                        </div>
                        {data?.url && (
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {data.url}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="w-8 h-8"
                          onClick={(e) => { e.stopPropagation(); downloadQr(unit.unit_id, unit.name); }}
                          title="Descargar QR"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="w-8 h-8"
                          onClick={(e) => { e.stopPropagation(); data?.url && copyToClipboard(data.url, unit.unit_id); }}
                          title="Copiar URL"
                        >
                          {copiedUrl === unit.unit_id ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="w-8 h-8"
                          onClick={(e) => { e.stopPropagation(); data?.url && openQrUrl(data.url); }}
                          title="Abrir menú"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Print Preview Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Imprimir Códigos QR
            </DialogTitle>
            <DialogDescription>
              Se imprimirán {selectedForPrint.length} códigos QR en formato de grilla (3 por fila)
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-96 border rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              {selectedForPrint.map((unitId) => {
                const unit = units.find(u => u.unit_id === unitId);
                const data = qrData[unitId];
                
                return (
                  <div key={unitId} className="text-center p-3 border border-dashed rounded-lg">
                    {data?.qr_image ? (
                      <img 
                        src={data.qr_image} 
                        alt={`QR ${unit?.name}`}
                        className="w-24 h-24 mx-auto mb-2"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-muted rounded mx-auto mb-2 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    )}
                    <p className="font-semibold text-sm">{unit?.name}</p>
                    <p className="text-xs text-muted-foreground">{unit?.space_name}</p>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={printQrs}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
